import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import app from '@src/helpers/app';
import ConfigurationException from '@src/exceptions/ConfigurationException';
import {ListPaginationHttpResponse, ListSortSettings} from '@src/@types/components/List';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {UserPreferencesKeys} from '@src/@types/services/UserPreferencesNamesResolver';
import {IListConfig} from '@src/@interfaces/components/List/IListConfig';
import useWebSocketForList from '@client/src/hooks/useWebSocketForList';
import useDebounce from '@src/hooks/useDebounce';

export default function ListRecordsProvider<Model extends IEmptyModel = IEmptyModel>(props: {
    uniqueListName: string,
    currentPage?: number,
    requestUri: string,
    disableFiltering?: boolean,
    searchTerm?: string | null,
    sortBy?: ListSortSettings,
    setCurrentPageNumber: (number: number) => void,
    queryKeyAdditionalShapeProps?: Record<string, any>,
    listenEventsToRefreshList?: IListConfig['listenEventsToRefreshList'],
    children: (queryResult: UseQueryResult<ListPaginationHttpResponse<Model>, Error>) => React.ReactNode,
    chosenFilterName?: string,
    chosenFilterId?: string | number,
}) {
    const {currentPage, listenEventsToRefreshList, disableFiltering = false} = props;
    const {chosenFilterId, chosenFilterName} = props;
    const setCurrentPageNumber = props.setCurrentPageNumber;

    const context = useContext(ListConfigContext);

    const getPref = (key: UserPreferencesKeys) => context?.controllerName ?
            app('auth').getUser()?.getModelPreferenceValue(context?.controllerName, key) :
            null;

    type Params = Parameters<NonNullable<typeof useWebSocketForList>>[0];
    const wsEventsToListen = useMemo<Params>(() => {
        if (!listenEventsToRefreshList) {
            return null;
        }

        return listenEventsToRefreshList.map(item => ({
            model: (item as { model: string }).model,
            channel: (item as { channel: string }).channel,
            event: item.events,
        })) as unknown as Params;
    }, [listenEventsToRefreshList]);

    const {lastMessages} = useWebSocketForList(wsEventsToListen);
    const [lastEventFired, setLastEventFired] = useState<number>(Date.now());
    const lastWSEventFiredAt = useDebounce(lastEventFired, 1500);

    useEffect(() => {
        if (lastMessages.length) {
            setLastEventFired(Date.now());
        }
    }, [lastMessages]);

    const queryKeyPerPage = getPref(UserPreferencesKeys.LIST_RECORDS_PER_PAGE);
    const queryKeyChosenFilter = getPref(UserPreferencesKeys.LIST_CHOSEN_FILTER);

    const queryKey = useMemo(() => {
        return app('queryKeys').getListOfExactModelRecordsQueryKey(
                context?.controllerName ?? '~unknown~',
                {
                    currentPage,
                    perPage: queryKeyPerPage,
                    disableFiltering,
                    chosenFilter: queryKeyChosenFilter,
                    chosenFilterId,
                    chosenFilterName,
                    searchTerm: props.searchTerm,
                    sortBy: props.sortBy,
                },
                {
                    getListUri: context?.config.getListUri,
                    requestUri: props.requestUri,
                    lastWSEventFiredAt,
                    ...(props.queryKeyAdditionalShapeProps ?? {}),
                }
        );
    }, [chosenFilterId, chosenFilterName, context?.config.getListUri, context?.controllerName, currentPage, disableFiltering, lastWSEventFiredAt, props.queryKeyAdditionalShapeProps, props.requestUri, props.searchTerm, props.sortBy, queryKeyChosenFilter, queryKeyPerPage]);

    const debouncedQueryKey = useDebounce(queryKey, 100);
    const queryResult = useQuery<ListPaginationHttpResponse<Model>, Error>(debouncedQueryKey, () => {
        if (!context || !lastWSEventFiredAt) {
            return Promise.reject();
        }

        if (!props.requestUri.length) {
            throw new ConfigurationException('Exceptions.configuration.model-list-request-uri-not-found');
        }

        return app('http').request(props.requestUri, {
            body: {
                'controller-name': context.controllerName,
                page: currentPage,
                ...(chosenFilterId ? {chosenFilterId} : {}),
                ...(chosenFilterName ? {chosenFilterName} : {}),
                ...(disableFiltering ? {disableFiltering: true} : {}),
                ...(props.searchTerm ? {searchTerm: props.searchTerm} : {}),
                ...(props.sortBy ? {sortBy: props.sortBy} : {}),
            }
        }).then(value => {
            if (value.current_page !== currentPage) {
                setCurrentPageNumber(value.current_page);
            }

            return value;
        });
    }, {
        enabled: Boolean(context) && Boolean(currentPage),
        useErrorBoundary: false,
        keepPreviousData: true,
        cacheTime: 5 * 60 * 1000,
    });

    const response = queryResult.data;
    useEffect(() => {
        if (response && currentPage && 'last_page' in response && response.last_page < currentPage) {
            setCurrentPageNumber(response.last_page);
        }
    }, [currentPage, response, setCurrentPageNumber]);

    return <>{props.children(queryResult)}</>;
}
